import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FaEdit, FaSave, FaTimes, FaPlus, FaTrash } from "react-icons/fa";

const AdminPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({ title: "", content: "" });
  const [newPolicyData, setNewPolicyData] = useState({
    title: "",
    content: "",
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await api.get("/policies");
      setPolicies(response.data);
    } catch (error) {
      toast.error("Không thể tải danh sách chính sách");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({ title: policy.title, content: policy.content });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/policies/${editingPolicy._id}`, formData);
      setEditingPolicy(null);
      fetchPolicies();
      window.dispatchEvent(new Event("policies_updated"));
    } catch (error) {
      toast.error("Lỗi khi cập nhật chính sách");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/policies", newPolicyData);
      toast.success("Thêm chính sách thành công!");
      setNewPolicyData({ title: "", content: "" });
      fetchPolicies();
      window.dispatchEvent(new Event("policies_updated"));
    } catch (error) {
      toast.error(error.response?.data || "Lỗi khi thêm chính sách");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Bạn có chắc chắn muốn xóa chính sách này? Thao tác này sẽ gỡ bỏ liên kết trên Navbar và Footer.",
      )
    )
      return;
    try {
      await api.delete(`/admin/policies/${id}`);
      toast.success("Đã xóa chính sách!");
      fetchPolicies();
      window.dispatchEvent(new Event("policies_updated"));
    } catch (error) {
      toast.error("Lỗi khi xóa chính sách");
    }
  };

  if (loading)
    return (
      <div className="p-8 text-center text-blue-600 font-bold">
        Đang tải cấu hình...
      </div>
    );

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-black text-gray-900">
            Quản lý Chính sách
          </h1>
          <p className="text-gray-600 mt-1">
            Thiết lập nội dung cho các trang chính sách của cửa hàng
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
        >
          <FaPlus />
          <span>Thêm chính sách mới</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {policies.map((policy) => (
          <div
            key={policy._id}
            className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow group"
          >
            <div className="mb-6">
              <h3 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">
                {policy.title}
              </h3>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-lg mt-2">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                  Slug:
                </span>
                <code className="text-xs text-blue-500 font-mono">
                  {policy.slug}
                </code>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-gray-50">
              <button
                onClick={() => handleEdit(policy)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors font-bold text-sm"
              >
                <FaEdit size={14} />
                <span>Chỉnh sửa</span>
              </button>
              <button
                onClick={() => handleDelete(policy._id)}
                className="px-3 py-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                title="Xóa chính sách"
              >
                <FaTrash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Thêm mới */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-[32px] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h2 className="text-xl font-black text-gray-900 uppercase">
                Thêm chính sách mới
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes size={18} />
              </button>
            </div>

            <form
              onSubmit={handleCreate}
              className="p-8 overflow-y-auto flex-1 space-y-6"
            >
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">
                  Tiêu đề chính sách
                </label>
                <input
                  type="text"
                  placeholder="Ví dụ: Chính sách bán sỉ..."
                  value={newPolicyData.title}
                  onChange={(e) =>
                    setNewPolicyData({
                      ...newPolicyData,
                      title: e.target.value,
                    })
                  }
                  className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">
                  Nội dung chi tiết
                </label>
                <textarea
                  placeholder="Nhập nội dung hiển thị trên trang chính sách..."
                  value={newPolicyData.content}
                  onChange={(e) =>
                    setNewPolicyData({
                      ...newPolicyData,
                      content: e.target.value,
                    })
                  }
                  className="w-full bg-gray-50 border border-gray-200 rounded-[24px] px-5 py-4 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all min-h-[350px] text-gray-700 leading-relaxed scrollbar-thin"
                  required
                />
              </div>

              <div className="pt-4 flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white rounded-[20px] py-4 font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/25 flex items-center justify-center gap-2 group"
                >
                  <span>Tạo chính sách</span>
                  <FaSave className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-10 bg-gray-100 text-gray-500 rounded-[20px] py-4.5 font-bold hover:bg-gray-200 transition-all"
                >
                  Đóng
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Chỉnh sửa */}
      {editingPolicy && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-[32px] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-blue-50/30">
              <h2 className="text-xl font-black text-blue-900 uppercase">
                Chỉnh sửa: {editingPolicy.title}
              </h2>
              <button
                onClick={() => setEditingPolicy(null)}
                className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full text-gray-400 hover:text-gray-600 transition-colors shadow-sm"
              >
                <FaTimes size={18} />
              </button>
            </div>

            <form
              onSubmit={handleUpdate}
              className="p-8 overflow-y-auto flex-1 space-y-6"
            >
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">
                  Tiêu đề
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1 uppercase tracking-wider">
                  Nội dung chính sách
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  className="w-full bg-gray-50 border border-gray-200 rounded-[24px] px-5 py-4 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all min-h-[400px] text-gray-700 leading-relaxed scrollbar-thin"
                  required
                />
              </div>

              <div className="pt-4 flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-red-600 text-white rounded-[20px] py-4 font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-500/25 flex items-center justify-center gap-2 group"
                >
                  <FaSave className="group-hover:scale-110 transition-transform" />
                  <span>Lưu thay đổi</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPolicy(null)}
                  className="px-10 bg-gray-100 text-gray-500 rounded-[20px] py-4.5 font-bold hover:bg-gray-200 transition-all"
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPolicies;
